<template>
  <div>
    <CategorySelectBox
      :categories="categories"
      :selected-category="selectedCategory"
      @change="setSelectedCategoryId"
    />
    <vue-perfect-scrollbar
      v-if="servicesList.length"
      ref="listWrapper"
      class="services-list"
    >
      <template v-for="service in servicesList">
        <GmbServiceChip
          :key="service.service_type_id"
          :name="getServiceName(service)"
          :value="service.selected"
          :delete-mode="service.deleted"
          :description="service.description"
          :price="getPrice(service.price, service.currency_code)"
          @revert-delete="handleRevertDelete(service)"
          @delete="handleDelete(service)"
          @input="handleSelect(service)"
        />
      </template>
    </vue-perfect-scrollbar>
    <GmbServiceCustomService
      ref="customService"
      :services-list="servicesList"
      :selected-category-id="selectedCategoryId"
      @custom-form-validation="isCustomServiceValid = $event"
      @change="handleCustomServiceChange"
    />
    <div class="services-dialog__actions">
      <ui-button color="primary" outlined @click="handleClose">
        {{ $t("message.common.cancel") }}
      </ui-button>
      <ui-button
        color="primary"
        class="services-dialog__confirm-button"
        :disabled="!isCustomServiceValid"
        @click="handleSave"
      >
        {{ $t("message.common.save") }}
      </ui-button>
    </div>
    <SectionLoader
      class="services-list__loader"
      :status="isLoading"
      :opacity="1"
    />
  </div>
</template>

<script>
import CategorySelectBox from "./GmbServicesCategorySelectBox.vue";
import GmbServiceChip from "@/components/FacilityDetails/GmbServices/GmbServiceChip.vue";
import GmbServiceCustomService from "@/components/FacilityDetails/GmbServices/GmbServiceCustomService.vue";
import gmbServices from "@/services/gmbServices";
import ServiceMerger from "@/helpers/gmbServices/service-merger";
import SectionLoader from "@/components/Loader/SectionLoader.vue";
import { SERVICE_ACTIONS_ENUM } from "@/enums/gmb-services.enum";
import PayloadManager from "@/helpers/gmbServices/payload-manager";

export default {
  name: "GmbServicesManager",
  components: {
    CategorySelectBox,
    GmbServiceChip,
    GmbServiceCustomService,
    SectionLoader
  },
  props: {
    selectedServices: {
      type: Array,
      default: () => []
    },
    categories: {
      type: Array,
      required: true
    },
    selectedCategory: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      selectedCategoryId: null,
      payload: [],
      servicesList: [],
      totalServicesList: [],
      isLoading: false,
      isCustomServiceValid: true
    };
  },
  created() {
    this.setSelectedCategoryId();
  },
  methods: {
    handleRevertDelete(service) {
      this.$emit("revert-delete", { item: service });
    },
    getServiceName(service) {
      return service.display_name || service.gmb_service_type.display_name;
    },
    mergeServices(selectedServices, newServices) {
      const serviceMerger = new ServiceMerger(this.selectedCategoryId);
      return serviceMerger.merge(selectedServices, newServices);
    },
    async getServiceList(params) {
      const {
        service_types: totalServicesList
      } = await gmbServices.getGmbServicesList(params);
      return totalServicesList;
    },
    getPrice(price, currency) {
      return price && currency ? `${price}${currency}` : undefined;
    },
    handleCustomServiceChange(customServices) {
      this.payload = customServices;
    },
    handleSave() {
      this.$emit("save", this.payload);
      this.$refs.customService.clearServicesList();
      this.payload = null;
    },
    setSelectedCategoryId(
      { id: categoryId } = this.selectedCategory
        ? { id: this.selectedCategory.id }
        : this.categories[0]
    ) {
      this.selectedCategoryId = categoryId;
    },
    handleDelete(service) {
      const payload = PayloadManager.getServiceItemsPayload(
        SERVICE_ACTIONS_ENUM.DELETE,
        service
      );
      this.$emit("delete", payload);
    },

    handleSelect(service) {
      this.$emit("service-select", service);
    },
    handleClose() {
      this.$emit("close");
    },
    setServicesList(totalServicesList) {
      this.servicesList = this.mergeServices(
        this.selectedServices,
        totalServicesList
      );
    }
  },
  watch: {
    async selectedCategoryId(currentCategoryId) {
      this.isLoading = true;
      this.totalServicesList = await this.getServiceList({
        categories_ids: currentCategoryId.toString()
      });
      this.setServicesList(this.totalServicesList);
      this.isLoading = false;
    },
    selectedServices() {
      this.setServicesList(this.totalServicesList);
    }
  }
};
</script>

<style lang="scss" scoped>
.services-list {
  display: flex;
  flex-wrap: wrap;
  gap: $spacing;
  height: 100%;
  min-height: 84px;
  max-height: 250px;
  overflow: hidden;
  flex-shrink: 0;
  &__loader {
    padding-bottom: $spacing * 7;
  }
}
.services-dialog {
  &__actions {
    display: flex;
    gap: $spacing;
    justify-content: center;
    flex-grow: 1;
    margin-bottom: $spacing * 3;
  }

  &__confirm-button {
    flex-grow: 1;
  }
}
</style>
